import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { excludePatchVersions } from "@components/NodeLink/versionHelpers"
import { GraphLinksFormAtom } from "@features/builder/UpdateMetadata/GraphLinks/atoms"
import { GraphLink } from "@features/builder/UpdateMetadata/GraphLinks/types"
import { useInitGraphLinks } from "@features/builder/UpdateMetadata/GraphLinks/useInitGraphLinks"
import { useGetFamilyBasesQuery } from "@store/services/baseGraphFamiliesService"
import { Autocomplete, Box, FormHelperText, TextField, Typography } from "@mui/material"
import { useAtom } from "jotai"

export const GraphLinksManager = () => {
  useInitGraphLinks()
  const { getValidationErrors } = useNodesValidation()
  const [links, setLinks] = useAtom(GraphLinksFormAtom)
  const handleVersionChange = (link: GraphLink) => (selectedVersion: string | null) => {
    setLinks((draft) => {
      const entry = draft.find(
        ({ linkedGraphFamily }) => linkedGraphFamily === link.linkedGraphFamily,
      )
      if (entry) {
        entry.selectedVersion = selectedVersion ?? undefined
      }
    })
  }

  return (
    !!links.length && (
      <Box
        data-testid="graph-links-manager"
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Graph Links
        </Typography>
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {links.map((link, idx) => (
            <GraphLinkRow
              key={link.linkedGraphFamily}
              link={link}
              onVersionChange={handleVersionChange(link)}
              errors={getValidationErrors("NODE_LINK", link.nodeIds)}
              first={idx === 0}
            />
          ))}
        </Box>
      </Box>
    )
  )
}

type GraphLinkRowProps = {
  link: GraphLink
  onVersionChange: (value: string | null) => void
  errors: ValidationState["errors"]
  first: boolean
}

const GraphLinkRow = ({ link, onVersionChange, errors, first }: GraphLinkRowProps) => {
  const { data, isFetching } = useGetFamilyBasesQuery({
    familyKey: link.linkedGraphFamily,
    roleHint: "build",
  })

  const filteredVersions = excludePatchVersions(data?.map((graph) => graph.version) ?? [])
  const isMultiVersion = link.usedVersion.length > 1
  const showingPlaceholder = isMultiVersion && !link.selectedVersion
  const autocompleteValue = link.selectedVersion ?? link.usedVersion.join(", ")
  const currentSingleVersion = !isMultiVersion ? link.selectedVersion ?? link.usedVersion[0] : null
  const options =
    currentSingleVersion && !filteredVersions.includes(currentSingleVersion)
      ? [currentSingleVersion, ...filteredVersions]
      : filteredVersions

  return (
    <Box
      data-testid="graph-link-row"
      sx={{
        display: "grid",
        alignItems: "center",
        columnGap: 2,
        rowGap: 0.5,
        gridTemplateColumns: "minmax(0, 1fr) auto",
      }}
    >
      <TextField
        fullWidth
        variant="standard"
        label={first ? "Graph Family" : null}
        value={link.linkedGraphFamily}
        InputProps={{ readOnly: true }}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: 0,
            marginTop: "0 !important",
          },
        }}
      />
      <Autocomplete
        options={options}
        data-testid="graph-link-autocomplete"
        disableClearable
        freeSolo={showingPlaceholder}
        forcePopupIcon
        loading={isFetching}
        value={autocompleteValue}
        onChange={(_e, newValue) => onVersionChange(newValue ?? null)}
        sx={{
          justifySelf: "end",
          width: `${Math.min(Math.max(link.usedVersion.join(", ").length, 12), 21)}ch`,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            variant="standard"
            label={first ? "Version" : null}
            inputProps={{
              ...params.inputProps,
              "aria-label": `Version for ${link.linkedGraphFamily}`,
              readOnly: showingPlaceholder,
              onFocus: (e) => e.target.select(),
            }}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0,
                marginTop: "0 !important",
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option === value}
      />
      <Box sx={{ gridColumn: "1 / -1" }}>
        {errors.map((err, idx) => (
          <FormHelperText key={idx} error>
            - {err.msg}
          </FormHelperText>
        ))}
      </Box>
    </Box>
  )
}
